import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { apiConfig } from '../config/auth0-config';

// Types for API responses
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface User {
  id: string;
  email: string;
  auth0Id: string | null;
  firstName: string | null;
  lastName: string | null;
  role: 'ADMIN' | 'MANAGER' | 'COACH' | 'CLIENT';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  permissions: string[];
  auth0Metadata: any;
  emailVerified: boolean;
  lastLogin: string | null;
  loginCount: number;
  auth0LastSync: string | null;
  profilePicture: string | null;
  phoneNumber: string | null;
  dateOfBirth?: Date | string | null;
  gender?: 'male' | 'female' | 'other' | 'prefer-not-to-say' | null;
  timezone: string;
  language: string;
  createdAt: string;
  updatedAt: string;
}

export interface Client {
  id: string;
  userId: string;
  tier: 'BASIC' | 'STANDARD' | 'PREMIUM' | 'ELITE';
  subscriptionType: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY' | null;
  paymentStatus: 'NONE' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'REFUNDED' | 'CANCELLED';
  agreedPrice: number | null;
  onboardingProgress: number;
  services: {
    training: boolean;
    nutrition: boolean;
    habitCoaching: boolean;
  } | null;
  assignedCoachId: string | null;
  goals: string[];
  healthConditions: string[];
  preferences: any;
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  } | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // Relationships
  user?: User;
  assignedCoach?: Coach;
}

export interface Coach {
  id: string;
  userId: string;
  specializations: string[];
  certifications: string[];
  experience: number | null;
  bio: string | null;
  hourlyRate: number | null;
  maxClients: number;
  isActive: boolean;
  performanceMetrics?: any;
  availability?: any;
  createdAt: string;
  updatedAt: string;
  // Relationships
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    profilePicture?: string;
    createdAt: string;
    timezone?: string;
    language?: string;
  };
  assignedClients: Array<{
    id: string;
    tier: string;
    onboardingProgress: number;
    paymentStatus: string;
    user: {
      firstName?: string;
      lastName?: string;
      email: string;
    };
  }>;
  _count?: {
    assignedClients: number;
  };
}

export interface Exercise {
  id: string;
  name: string;
  description?: string;
  type: 'compound' | 'isolation' | 'cardio';
  repRange: [number, number];
  sets?: number;
  restTime?: number;
  difficulty: number;
  rating: number;
  danger: number;
  symmetryMode: 'symmetrical' | 'unilateral' | 'bilateral';
  muscleActivations: { [muscleGroupId: string]: number };
  equipment: { [equipmentId: string]: number };
  targetAdaptations: { [adaptationId: string]: number };
  instructions: string[];
  techniqueChecklist: string[];
  videoUrl?: string;
  imageUrl?: string;
  tags?: string[];
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface MuscleGroup {
  id: string;
  name: string;
  split: boolean;
  category?: string;
  subCategory?: string;
  description?: string;
  anatomicalRegion?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Equipment {
  id: string;
  name: string;
  category: 'Cardio' | 'Strength' | 'Functional' | 'Accessories' | 'Technology';
  brand: string;
  model: string;
  acquisitionDate: string;
  acquisitionCost: number;
  currentValue: number;
  status: 'Available' | 'In Use' | 'Maintenance' | 'Out of Order' | 'Retired';
  condition: 'Excellent' | 'Good' | 'Fair' | 'Poor';
  location: string;
  maxUsers: number;
  currentUsers: number;
  maintenanceSchedule: 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Annually';
  lastMaintenance: string;
  nextMaintenance: string;
  warrantyExpiry?: string;
  notes: string;
  utilization: number;
  specifications: {
    weight?: number;
    dimensions?: string;
    powerRequirement?: string;
    capacity?: string;
  };
  attachments: string[];
  qrCode?: string;
  serialNumber: string;
}

export interface Session {
  id: string;
  coachId: string;
  clientId: string;
  programId?: string;
  type: 'TRAINING' | 'NUTRITION' | 'CHECK_IN' | 'ASSESSMENT';
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  scheduledDateTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  duration: number; // in minutes
  location: 'IN_PERSON' | 'VIRTUAL' | 'PHONE';
  sessionNotes?: string;
  clientFeedback?: string;
  coachRating?: number;
  clientRating?: number;
  exercises?: any[];
  measurements?: any[];
  nextSessionNotes?: string;
  createdAt: string;
  updatedAt: string;
  // Relationships
  coach?: Coach;
  client?: Client;
}

class ApiService {
  private api: AxiosInstance;
  private getAccessToken?: () => Promise<string>;
  private cache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();
  private pendingRequests: Map<string, Promise<any>> = new Map();

  constructor() {
    this.api = axios.create({
      baseURL: apiConfig.baseURL,
      timeout: apiConfig.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  // Set the Auth0 token getter function
  setTokenGetter(getToken: () => Promise<string>) {
    this.getAccessToken = getToken;
  }

  // Get current auth token (public method)
  async getCurrentToken(): Promise<string | null> {
    if (this.getAccessToken) {
      try {
        return await this.getAccessToken();
      } catch (error) {
        console.warn('Failed to get current token:', error);
        return null;
      }
    }
    return null;
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      async (config) => {
        if (this.getAccessToken) {
          try {
            const token = await this.getAccessToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (error) {
            console.warn('Failed to get access token:', error);
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          console.warn('API returned 401 Unauthorized, attempting token refresh...');
          
          // Prevent infinite retry loops by checking if this is already a retry
          const isRetry = error.config && (error.config as any)._isRetry;
          
          if (isRetry) {
            console.warn('Request already retried once, redirecting to login');
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }
          
          // Try to refresh the token before redirecting
          if (this.getAccessToken) {
            try {
              const newToken = await this.getAccessToken();
              if (newToken && error.config) {
                // Mark this as a retry to prevent infinite loops
                (error.config as any)._isRetry = true;
                
                // Retry the request with the new token
                error.config.headers.Authorization = `Bearer ${newToken}`;
                return this.api.request(error.config);
              }
            } catch (tokenError: any) {
              console.error('Token refresh failed:', tokenError);
              
              // Check if it's a refresh token expiration
              if (tokenError.message === 'REFRESH_TOKEN_EXPIRED') {
                console.log('Refresh token expired, redirecting to login...');
                // Only redirect to login if we're not already on the login page
                if (!window.location.pathname.includes('/login')) {
                  window.location.href = '/login';
                }
                return Promise.reject(error);
              }
            }
          }
          
          // For other 401 errors, don't immediately redirect - let the component handle it
          console.warn('Authentication failed, but not redirecting automatically');
        }
        return Promise.reject(error);
      }
    );
  }

  // Cache utilities
  private getCacheKey(method: string, url: string, data?: any): string {
    return `${method}:${url}:${data ? JSON.stringify(data) : ''}`;
  }

  private isCacheValid(entry: { timestamp: number; ttl: number }): boolean {
    return Date.now() - entry.timestamp < entry.ttl;
  }

  private clearExpiredCache(): void {
    for (const [key, entry] of this.cache.entries()) {
      if (!this.isCacheValid(entry)) {
        this.cache.delete(key);
      }
    }
  }

  // Generic request method with caching for GET requests
  async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    url: string,
    data?: any,
    cacheTtl?: number // Cache TTL in milliseconds, only for GET requests
  ): Promise<ApiResponse<T>> {
    const cacheKey = this.getCacheKey(method, url, data);
    
    // Only cache GET requests
    if (method === 'GET' && cacheTtl) {
      // Check if there's a valid cached response
      const cached = this.cache.get(cacheKey);
      if (cached && this.isCacheValid(cached)) {
        console.log(`🗄️ Cache hit for ${url} (${Math.round((Date.now() - cached.timestamp) / 1000)}s old)`);
        return cached.data;
      }

      // Check if there's already a pending request for this endpoint
      const pending = this.pendingRequests.get(cacheKey);
      if (pending) {
        console.log(`⏳ Returning pending request for ${url} - preventing duplicate call`);
        return pending;
      }
      
      console.log(`🌐 Making fresh request to ${url} (no cache or expired)`);
    }

    try {
      const requestPromise = this.api.request({
        method,
        url,
        data,
      }).then(response => response.data);

      // Store pending request
      if (method === 'GET' && cacheTtl) {
        this.pendingRequests.set(cacheKey, requestPromise);
      }

      const result = await requestPromise;

      // Cache successful GET responses
      if (method === 'GET' && cacheTtl && result.success) {
        this.cache.set(cacheKey, {
          data: result,
          timestamp: Date.now(),
          ttl: cacheTtl
        });
        // Clean up expired cache entries periodically
        if (this.cache.size > 100) {
          this.clearExpiredCache();
        }
      }

      // Remove from pending requests
      if (method === 'GET' && cacheTtl) {
        this.pendingRequests.delete(cacheKey);
      }

      return result;
    } catch (error) {
      // Remove from pending requests on error
      if (method === 'GET' && cacheTtl) {
        this.pendingRequests.delete(cacheKey);
      }

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          error: error.response?.data?.message || error.message,
        };
      }
      return {
        success: false,
        error: 'An unexpected error occurred',
      };
    }
  }

  // Health endpoints
  async healthCheck() {
    return this.request('GET', '/api/health');
  }

  async detailedHealth() {
    return this.request('GET', '/api/health/detailed');
  }

  // Auth endpoints
  async getAuthStatus() {
    return this.request('GET', '/api/auth/status');
  }

  async getProfile() {
    return this.request<User>('GET', '/api/auth/profile');
  }

  // Client management (for coaches)
  async getClients(params?: { 
    page?: number; 
    limit?: number; 
    tier?: string; 
    coachId?: string;
    search?: string;
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<{ clients: Client[]; total: number; page: number; limit: number }>
      ('GET', `/api/clients?${query}`);
  }

  async getClientById(id: string) {
    return this.request<Client>('GET', `/api/clients/${id}`);
  }

  async createClient(clientData: Partial<Client>) {
    return this.request<Client>('POST', '/api/clients', clientData);
  }

  async updateClient(id: string, clientData: Partial<Client>) {
    return this.request<Client>('PUT', `/api/clients/${id}`, clientData);
  }

  // Coach endpoints
  async getCoachProfile() {
    return this.request<{ data: Coach }>('GET', '/api/coaches/profile');
  }

  async updateCoachProfile(data: Partial<Coach>) {
    return this.request<{ data: Coach; message: string }>('PUT', '/api/coaches/profile', data);
  }

  async getCoachClients(params?: { page?: number; limit?: number; coachId?: string }) {
    const query = new URLSearchParams(params as any).toString();
    
    if (!params?.coachId) {
      throw new Error('Coach ID is required to fetch clients. Please ensure coach profile is loaded first.');
    }
    
    const coachId = params.coachId;
    console.log('📞 API: Fetching clients for coach ID:', coachId);
    
    // Cache for 30 seconds to prevent rapid repeated calls
    return this.request<{ data: Client[]; pagination: any }>
      ('GET', `/api/coaches/${coachId}/clients?${query}`, undefined, 30000);
  }

  // Exercise management
  async getExercises(params?: { 
    page?: number; 
    limit?: number; 
    type?: string; 
    difficulty?: string;
    search?: string;
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<{ exercises: Exercise[]; total: number; page: number; limit: number }>
      ('GET', `/api/exercises?${query}`);
  }

  async getExerciseById(id: string) {
    return this.request<Exercise>('GET', `/api/exercises/${id}`);
  }

  async createExercise(exerciseData: Partial<Exercise>) {
    return this.request<Exercise>('POST', '/api/exercises', exerciseData);
  }

  async updateExercise(id: string, exerciseData: Partial<Exercise>) {
    return this.request<Exercise>('PUT', `/api/exercises/${id}`, exerciseData);
  }

  async deleteExercise(id: string) {
    return this.request('DELETE', `/api/exercises/${id}`);
  }

  async searchExercises(query: string, filters?: { type?: string; muscleGroup?: string; equipment?: string }) {
    const params = new URLSearchParams({ q: query });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }
    return this.request<{exercises: Exercise[]; suggestions: string[]; filters: any}>('GET', `/api/exercises/search?${params.toString()}`);
  }

  // Muscle Groups management
  async getMuscleGroups(params?: { 
    category?: string; 
    region?: string 
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<MuscleGroup[]>('GET', `/api/muscle-groups?${query}`);
  }

  // Equipment management
  async getEquipment(params?: { 
    page?: number; 
    limit?: number; 
    type?: string; 
    status?: string;
    condition?: string;
    location?: string;
    search?: string;
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<{ data: Equipment[]; pagination?: any }>('GET', `/api/equipment?${query}`);
  }

  async getEquipmentById(id: string) {
    return this.request<Equipment>('GET', `/api/equipment/${id}`);
  }

  async createEquipment(equipmentData: Partial<Equipment>) {
    return this.request<Equipment>('POST', '/api/equipment', equipmentData);
  }

  async updateEquipment(id: string, equipmentData: Partial<Equipment>) {
    return this.request<Equipment>('PUT', `/api/equipment/${id}`, equipmentData);
  }

  async deleteEquipment(id: string) {
    return this.request('DELETE', `/api/equipment/${id}`);
  }

  // Program endpoints
  async getPrograms(params?: {
    page?: number;
    limit?: number;
    coachId?: string;
    clientId?: string;
    status?: string;
    search?: string;
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<PaginatedResponse<any>>('GET', `/api/programs?${query}`);
  }

  async getProgramById(id: string) {
    return this.request<any>('GET', `/api/programs/${id}`);
  }

  async getProgramWorkouts(programId: string) {
    return this.request<any>('GET', `/api/programs/${programId}/workouts`);
  }

  async createProgram(programData: any) {
    return this.request<any>('POST', '/api/programs', programData);
  }

  async updateProgram(id: string, programData: any) {
    return this.request<any>('PUT', `/api/programs/${id}`, programData);
  }

  async deleteProgram(id: string) {
    return this.request('DELETE', `/api/programs/${id}`);
  }

  async assignWorkoutDates(programId: string, workoutAssignments: Array<{
    workoutId: string;
    assignedDate: string;
  }>) {
    return this.request('PUT', `/api/programs/${programId}/workouts/assign-dates`, {
      workoutAssignments
    });
  }

  // Program generation
  async generateProgram(request: any) {
    return this.request<any>('POST', '/api/programs/generate', request);
  }

  // Session management
  async getSessions(params?: {
    page?: number;
    limit?: number;
    coachId?: string;
    clientId?: string;
    status?: string;
    type?: string;
    startDate?: string;
    endDate?: string;
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<{ data: Session[]; pagination: any }>('GET', `/api/sessions?${query}`);
  }

  async getSessionById(id: string) {
    return this.request<{ data: Session }>('GET', `/api/sessions/${id}`);
  }

  async createSession(sessionData: Partial<Session>) {
    return this.request<{ data: Session }>('POST', '/api/sessions', sessionData);
  }

  async updateSession(id: string, sessionData: Partial<Session>) {
    return this.request<{ data: Session }>('PUT', `/api/sessions/${id}`, sessionData);
  }

  async deleteSession(id: string) {
    return this.request('DELETE', `/api/sessions/${id}`);
  }

  async completeSession(id: string, completionData: {
    sessionNotes?: string;
    exercises?: any[];
    measurements?: any[];
    nextSessionNotes?: string;
  }) {
    return this.request<{ data: Session }>('PATCH', `/api/sessions/${id}/complete`, completionData);
  }

  // Dashboard/Statistics
  async getDashboardStats() {
    return this.request('GET', '/api/coaches/dashboard/stats');
  }

  async getClientProgress(clientId: string, params?: {
    startDate?: string;
    endDate?: string;
    metric?: string;
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request(`GET`, `/api/clients/${clientId}/progress?${query}`);
  }
}

// Create and export singleton instance
export const apiService = new ApiService();
export default apiService;
